import { MAX_OPEN_TABS } from '../../constants';
import type {
  AgentForgeStore,
  OpenTab,
  SliceCreator,
  TabsActions,
} from './types';

const upsertTab = (
  tabs: OpenTab[],
  next: OpenTab,
  protectedSessionIds: Set<string>
): { tabs: OpenTab[]; evicted: string[] } => {
  const idx = tabs.findIndex((t) => t.sessionId === next.sessionId);
  if (idx === -1) {
    const kept = [...tabs, next];
    const evicted: string[] = [];
    while (kept.length > MAX_OPEN_TABS) {
      // Never discard unsent intent merely because the user opened another
      // chat. If every older tab is protected, temporarily exceed the visual
      // cap; a later safe open/close will bring it back down.
      const candidate = kept.findIndex(
        (tab) =>
          tab.sessionId !== next.sessionId &&
          !protectedSessionIds.has(tab.sessionId)
      );
      if (candidate < 0) break;
      evicted.push(kept[candidate].sessionId);
      kept.splice(candidate, 1);
    }
    return {
      tabs: kept,
      evicted,
    };
  }
  const updated = [...tabs];
  updated[idx] = next;
  return { tabs: updated, evicted: [] };
};

const protectedSessions = (s: AgentForgeStore): Set<string> => {
  const protectedIds = new Set<string>();
  for (const tab of s.openTabs) {
    const sessionId = tab.sessionId;
    if (
      tab.status === 'streaming' ||
      !!s.activeTurnsBySession[sessionId] ||
      (s.queuedBySession[sessionId]?.length ?? 0) > 0 ||
      !!s.activeSubmissionBySession[sessionId] ||
      !!s.draftBySession[sessionId]?.trim() ||
      s.streamingDraft?.sessionId === sessionId ||
      !!s.uploadProgressBySession[sessionId]
    ) {
      protectedIds.add(sessionId);
    }
  }
  return protectedIds;
};

export const createTabsSlice: SliceCreator<TabsActions> = (set) => ({
  openTab: (tab) =>
    set((s) => {
      const { tabs, evicted } = upsertTab(
        s.openTabs,
        tab,
        protectedSessions(s)
      );
      if (evicted.length === 0) {
        return {
          openTabs: tabs,
          activeSessionId: tab.sessionId,
          mode: s.mode === 'minimized' ? 'floating' : s.mode,
          draftOpen: false,
        };
      }
      const evictedIds = new Set(evicted);
      const messages = { ...s.messagesBySession };
      const drafts = { ...s.draftBySession };
      const queued = { ...s.queuedBySession };
      const submissions = { ...s.activeSubmissionBySession };
      const activeTurns = { ...s.activeTurnsBySession };
      const turnScopes = { ...s.turnScopeBySession };
      const uploadProgress = { ...s.uploadProgressBySession };
      evictedIds.forEach((sessionId) => {
        delete messages[sessionId];
        delete drafts[sessionId];
        delete queued[sessionId];
        delete submissions[sessionId];
        delete activeTurns[sessionId];
        delete turnScopes[sessionId];
        delete uploadProgress[sessionId];
      });
      return {
        openTabs: tabs,
        activeSessionId: tab.sessionId,
        mode: s.mode === 'minimized' ? 'floating' : s.mode,
        draftOpen: false,
        messagesBySession: messages,
        draftBySession: drafts,
        queuedBySession: queued,
        activeSubmissionBySession: submissions,
        activeTurnsBySession: activeTurns,
        turnScopeBySession: turnScopes,
        uploadProgressBySession: uploadProgress,
        streamingDraft:
          s.streamingDraft && evictedIds.has(s.streamingDraft.sessionId)
            ? null
            : s.streamingDraft,
      };
    }),

  closeTab: (sessionId) =>
    set((s) => {
      const remaining = s.openTabs.filter((t) => t.sessionId !== sessionId);
      const wasActive = s.activeSessionId === sessionId;
      const { [sessionId]: _, ...remainingMessages } = s.messagesBySession;
      const remainingDrafts = { ...s.draftBySession };
      const remainingQueued = { ...s.queuedBySession };
      const remainingSubmissions = { ...s.activeSubmissionBySession };
      const remainingActiveTurns = { ...s.activeTurnsBySession };
      const remainingTurnScopes = { ...s.turnScopeBySession };
      const remainingUploadProgress = { ...s.uploadProgressBySession };
      delete remainingDrafts[sessionId];
      delete remainingQueued[sessionId];
      delete remainingSubmissions[sessionId];
      delete remainingActiveTurns[sessionId];
      delete remainingTurnScopes[sessionId];
      delete remainingUploadProgress[sessionId];
      // If the closed tab is the one we were streaming, drop the draft.
      const isStreamingThisTab = s.streamingDraft?.sessionId === sessionId;
      return {
        openTabs: remaining,
        activeSessionId: wasActive
          ? (remaining[remaining.length - 1]?.sessionId ?? null)
          : s.activeSessionId,
        messagesBySession: remainingMessages,
        draftBySession: remainingDrafts,
        queuedBySession: remainingQueued,
        activeSubmissionBySession: remainingSubmissions,
        activeTurnsBySession: remainingActiveTurns,
        turnScopeBySession: remainingTurnScopes,
        uploadProgressBySession: remainingUploadProgress,
        streamingDraft: isStreamingThisTab ? null : s.streamingDraft,
      };
    }),

  setActive: (sessionId) => set({ activeSessionId: sessionId }),

  setTabStatus: (sessionId, status) =>
    set((s) => ({
      openTabs: s.openTabs.map((t) =>
        t.sessionId === sessionId ? { ...t, status } : t
      ),
    })),

  setTabTitle: (sessionId, title) =>
    set((s) => ({
      openTabs: s.openTabs.map((t) =>
        t.sessionId === sessionId ? { ...t, title } : t
      ),
    })),

  migrateSession: (fromId, toId) =>
    set((s) => {
      if (fromId === toId) return s;
      const messages = { ...s.messagesBySession };
      if (messages[fromId]) {
        messages[toId] = messages[fromId];
        delete messages[fromId];
      }
      const seen = new Set<string>();
      const tabs = s.openTabs
        .map((t) => (t.sessionId === fromId ? { ...t, sessionId: toId } : t))
        .filter((t) => {
          if (seen.has(t.sessionId)) return false;
          seen.add(t.sessionId);
          return true;
        });
      const drafts = { ...s.draftBySession };
      if (drafts[fromId]) {
        drafts[toId] = drafts[fromId];
        delete drafts[fromId];
      }
      const queued = { ...s.queuedBySession };
      if (queued[fromId]) {
        // Turns already waiting on the stale session were submitted before
        // anything that joined the replacement session, so they stay first.
        queued[toId] = [...queued[fromId], ...(queued[toId] ?? [])];
        delete queued[fromId];
      }
      const submissions = { ...s.activeSubmissionBySession };
      if (submissions[fromId]) {
        submissions[toId] = submissions[fromId];
        delete submissions[fromId];
      }
      const activeTurns = { ...s.activeTurnsBySession };
      if (activeTurns[fromId]) {
        activeTurns[toId] = activeTurns[fromId];
        delete activeTurns[fromId];
      }
      const turnScopes = { ...s.turnScopeBySession };
      if (turnScopes[fromId]) {
        turnScopes[toId] = turnScopes[fromId];
        delete turnScopes[fromId];
      }
      const uploadProgress = { ...s.uploadProgressBySession };
      if (uploadProgress[fromId]) {
        uploadProgress[toId] = uploadProgress[fromId];
        delete uploadProgress[fromId];
      }
      return {
        messagesBySession: messages,
        openTabs: tabs,
        draftBySession: drafts,
        queuedBySession: queued,
        activeSubmissionBySession: submissions,
        activeTurnsBySession: activeTurns,
        turnScopeBySession: turnScopes,
        uploadProgressBySession: uploadProgress,
        streamingDraft:
          s.streamingDraft?.sessionId === fromId
            ? { ...s.streamingDraft, sessionId: toId }
            : s.streamingDraft,
        activeSessionId:
          s.activeSessionId === fromId ? toId : s.activeSessionId,
      };
    }),
});
